import { Link } from 'react-router-dom'
import { Correct } from '../../components/Correct'
import { LineIcon } from '../../components/LineIcon'
import { Avatar } from '../../components/Avatar'
import { formatDate, formatMoney, formatRelative } from '../../lib/format'
import type { Engagement, Signal } from '../../types/deals'
import type { ContactView } from '../../types/contacts'

// The account news as a feed: every signal read from the threads and the contact watch, newest first, an icon
// per signal type, how long ago, the person and the pursuit it sits on as links, and a tint for the severity.
// Scrolls inside its panel so the newest is always in the corner of the page.

const TYPE_WORDS: Record<string, string> = {
  'amount-mentioned': 'Amount mentioned', 'sow-sent': 'SOW sent', 'proposal-sent': 'Proposal sent', intro: 'Introduction', 'next-step': 'Next step',
  decision: 'Decision', invoice: 'Invoice', meeting: 'Meeting', dormant: 'Gone quiet', 'job-change': 'May have moved',
  // the public feeds (PL-216): each carries its page
  'bankruptcy-filing': 'Bankruptcy filing', 'default-notice': 'Default notice', 'going-concern': 'Going-concern doubt', 'officer-change': 'Officer change',
}
const OPEN = new Set(['lead', 'dormant'])

export function Newsfeed({ signals, contacts, engagements, onOpenPerson, now = new Date() }: {
  signals: Signal[]; contacts: ContactView[]; engagements: Engagement[]; onOpenPerson: (personId: string) => void; now?: Date
}) {
  const person = new Map(contacts.map((c) => [c.person.personId, c]))
  const pursuit = new Map(engagements.map((e) => [e.engagementId, e]))
  const feed = [...signals].sort((a, b) => b.occurredOn.localeCompare(a.occurredOn))
  return (
    <section className="card newsfeed-card" aria-label="Account news">
      <div className="card-head">
        <div>
          <h2>Newsfeed</h2>
          <p className="muted">{feed.length === 0 ? 'Nothing flagged yet.' : `${feed.length} signal${feed.length === 1 ? '' : 's'} read from the threads and the contact watch, newest first.`}</p>
        </div>
      </div>
      {feed.length === 0 ? (
        <p className="muted small">Read your threads for leads and opportunities, or refresh relationships on Contacts; what is found lands here.</p>
      ) : (
        <ol className="feed">
          {feed.map((s) => {
            const p = s.personId ? person.get(s.personId) : null
            const e = s.engagementId ? pursuit.get(s.engagementId) : null
            const route = e ? `${OPEN.has(e.stage) ? '/leads' : '/opportunities'}/${encodeURIComponent(e.engagementId)}` : null
            return (
              <li key={s.signalId} className={`feed-item sev-${s.severity}`}>
                <span className={`feed-icon type-${s.type}`}><LineIcon name={s.type} size={16} /></span>
                <div className="feed-body">
                  <div className="feed-head">
                    <strong>{TYPE_WORDS[s.type] ?? s.type}</strong>
                    <time className="muted small" dateTime={s.occurredOn} title={formatDate(s.occurredOn)}>{formatRelative(s.occurredOn, now)}</time>
                    <Correct kind="signal" entityId={s.signalId} entityLabel={s.headline} currentValue={s.type} compact />
                  </div>
                  <p className="feed-headline">{s.headline}{s.amount ? <span className="feed-amount"> {formatMoney(s.amount)}</span> : null}</p>
                  {(s.detail || s.url) && (
                    <p className="feed-detail muted small">
                      {s.detail}{s.url && <>{s.detail ? ' · ' : ''}<a href={s.url} target="_blank" rel="noreferrer" title={s.url}>the page</a></>}
                    </p>
                  )}
                  {(p || e) && (
                    <p className="feed-links small">
                      {p && <button className="feed-link" onClick={() => onOpenPerson(p.person.personId)}><Avatar name={p.person.name} photoUrl={p.person.photoUrl} persona={p.person.persona} role={p.person.role} size={18} />{p.person.name}</button>}
                      {e && route && <Link className="feed-link" to={route}><LineIcon name="pursuit" size={13} />{e.subject}</Link>}
                    </p>
                  )}
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
